import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Settings, Save, Loader2, Search, Newspaper, Activity, Shield, RefreshCw, Timer, Clock, Zap, TrendingUp, TrendingDown, BarChart3, Brain, AlertTriangle, Rss, Layers, ShieldAlert, Info, Crosshair } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useEffect } from "react";
import ModuleCTemplatePreview from "@/components/ModuleCTemplatePreview";
import { toast } from "sonner";

const REFRESH_OPTIONS = [
  { value: 15, label: "15 秒" },
  { value: 30, label: "30 秒" },
  { value: 60, label: "1 分钟" },
  { value: 120, label: "2 分钟" },
  { value: 300, label: "5 分钟" },
  { value: 600, label: "10 分钟" },
  { value: 900, label: "15 分钟" },
  { value: 1800, label: "30 分钟" },
  { value: 3600, label: "1 小时" },
  { value: 0, label: "不自动刷新" },
];

function RefreshSelect({ label, hint, value, onChange }: { label: string; hint: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
        <SelectTrigger className="h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {REFRESH_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={String(opt.value)}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-[10px] text-muted-foreground">{hint}</p>
    </div>
  );
}

export default function Config() {
  const { data: config, isLoading, refetch } = trpc.config.get.useQuery();
  const updateConfig = trpc.config.update.useMutation({
    onSuccess: () => {
      toast.success("配置已保存，其他设备刷新页面后生效", { duration: 4000 });
      refetch();
    },
    onError: (err) => toast.error(`保存失败: ${err.message}`),
  });

  // [Fix] tradeMode 从 interface_config 读取（system_config 表没有该字段）
  const { data: ifConfig, refetch: refetchIfConfig } = trpc.interfaceConfig.get.useQuery(undefined, { staleTime: 10000 });
  const saveIfConfig = trpc.interfaceConfig.save.useMutation({
    onSuccess: () => { refetchIfConfig(); },
    onError: (err: any) => toast.error(`交易模式保存失败: ${err.message}`),
  });

  const [form, setForm] = useState<Record<string, any>>({});
  const [tradeMode, setTradeModeState] = useState<string>('simulation');
  const [tradeModeInitialized, setTradeModeInitialized] = useState(false);
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);
  const [liveConfirmStep, setLiveConfirmStep] = useState(0); // 0=未开始 1=第一步 2=第二步
  const [pendingTradeMode, setPendingTradeMode] = useState<string | null>(null);
  const [authorizing, setAuthorizing] = useState(false);

  useEffect(() => {
    if (config) {
      setForm({ ...config });
    }
  }, [config]);

  // [Fix] 从 interface_config 初始化 tradeMode，只初始化一次
  useEffect(() => {
    if (ifConfig && !tradeModeInitialized) {
      setTradeModeState((ifConfig as any).tradeMode || 'simulation');
      setTradeModeInitialized(true);
    }
  }, [ifConfig, tradeModeInitialized]);

  const authorizeMutation = trpc.tradingProxy.authorizeLiveTrading.useMutation();
  const revokeMutation = trpc.tradingProxy.revokeLiveTrading.useMutation();
  const { data: authorizeStatus, refetch: refetchAuthStatus } = trpc.tradingProxy.authorizeStatus.useQuery(
    undefined,
    { refetchInterval: 10000, enabled: tradeMode === 'smtq' }
  );

  // 切换到实盘模式时弹出确认弹窗，其他模式直接切换
  const handleTradeModeChange = (newMode: string) => {
    if (newMode === 'smtq') {
      setPendingTradeMode(newMode);
      setLiveConfirmStep(1);
      setShowLiveConfirm(true);
    } else {
      // 切换到非实盘时自动撤销授权
      if (tradeMode === 'smtq') {
        revokeMutation.mutate(undefined, {
          onSuccess: () => toast.success('已撤销实盘授权，切回模拟模式'),
          onError: (e) => toast.error('撤销授权失败: ' + e.message),
        });
      }
      setTradeModeState(newMode);
      saveIfConfig.mutate({ tradeMode: newMode } as any);
    }
  };

  // 实盘授权确认流程
  const handleLiveAuthorizeConfirm = async () => {
    if (liveConfirmStep === 1) {
      setLiveConfirmStep(2);
      return;
    }
    // 第二步：保存配置 + 通知引擎写授权文件
    setAuthorizing(true);
    try {
      // 先保存配置到DB
      const { id, userId, createdAt, updatedAt, ...updates } = form;
      await updateConfig.mutateAsync(updates);
      // 再通知引擎授权
      const result = await authorizeMutation.mutateAsync({ mode: 'LIVE_TRADE' });
      if (result.success) {
        setTradeModeState('smtq');
        saveIfConfig.mutate({ tradeMode: 'smtq' } as any);
        toast.success('✅ 实盘授权成功，引擎已切换为纯实盘模式（仅底仓T+0）', { duration: 6000 });
        refetchAuthStatus();
      } else {
        toast.error('引擎授权失败: ' + (result.error || '未知错误'));
        // 回滚DB
        setField('tradeMode', tradeMode || 'simulation');
      }
    } catch (e: any) {
      toast.error('授权失败: ' + e.message);
    } finally {
      setAuthorizing(false);
      setShowLiveConfirm(false);
      setLiveConfirmStep(0);
      setPendingTradeMode(null);
    }
  };

  const handleSave = () => {
    const { id, userId, createdAt, updatedAt, ...updates } = form;
    updateConfig.mutate(updates);
  };

  const setField = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // [Steven 6/2] LLM 自动应用配置 (10:00 / 13:15)
  // [Steven 2026-06-03 React #310 修复] hooks 必须无条件调用, 移到 isLoading return 之前
  const { data: expertCfg, refetch: refetchExpertCfg } = (trpc as any).trading?.expertAdviceConfig?.useQuery?.(undefined, { staleTime: 10000 }) || { data: null, refetch: () => {} };
  const saveExpertCfg = (trpc as any).trading?.setExpertAdviceConfig?.useMutation?.({
    onSuccess: () => { toast.success('LLM 自动应用配置已保存'); refetchExpertCfg(); },
    onError: (e: any) => toast.error(e?.message || '保存失败'),
  });
  const [expertEnabled, setExpertEnabled] = useState<boolean>(false);
  const [expertMorningTime, setExpertMorningTime] = useState<string>('10:00');
  const [expertAfternoonTime, setExpertAfternoonTime] = useState<string>('13:15');
  const [expertConfMin, setExpertConfMin] = useState<number>(0.75);
  const [expertRevertPnl, setExpertRevertPnl] = useState<number>(-200);
  useEffect(() => {
    if (expertCfg) {
      setExpertEnabled(!!expertCfg.enabled);
      setExpertMorningTime(expertCfg.morningTime || '10:00');
      setExpertAfternoonTime(expertCfg.afternoonTime || '13:15');
      setExpertConfMin(Number(expertCfg.confidenceMin) || 0.75);
      setExpertRevertPnl(Number(expertCfg.revertPnlThreshold) || -200);
    }
  }, [expertCfg]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Settings className="h-5 w-5 text-primary" />
            参数配置中心
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            统一管理所有模块的策略参数，实时生效
          </p>
        </div>
        <Button onClick={handleSave} disabled={updateConfig.isPending}>
          {updateConfig.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          保存配置
        </Button>
      </div>

      {/* ===== [Steven 6/2] LLM 自动优化策略 (10:00 / 13:15) ===== */}
      <Card className="bg-card border-2 border-blue-500/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <span className="text-2xl">🤖</span>
            <span className="text-base font-bold text-blue-400">LLM 自动优化策略 (定时数据分析 + 自动应用)</span>
            <Badge className={(expertEnabled ? 'bg-green-500' : 'bg-gray-500') + ' text-white ml-2'}>
              {expertEnabled ? '已启用' : '未启用 (灰度)'}
            </Badge>
          </CardTitle>
          <CardDescription className="text-xs">
            交易日 10:00 / 13:15 (可配) 自动调用 qwen-plus + qwen-vl K 线分析, 满足 confidence ≥ 0.75 直接应用参数调整,
            应用后 30 分钟内 PnL 跌破阈值 (-200元) 自动 revert.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <label className="text-xs flex items-center gap-2">
                <input type="checkbox" checked={expertEnabled} onChange={e => setExpertEnabled(e.target.checked)} />
                <span>启用 LLM 自动应用 (默认关, 灰度)</span>
              </label>
              <div className="text-[10px] text-muted-foreground">关闭时 cron 不触发, 只生成 advice (UI banner 仍可手动采用)</div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">早盘触发时间 (HH:MM)</label>
              <Input type="text" placeholder="10:00" className="h-8 text-xs mt-1"
                value={expertMorningTime}
                onChange={e => setExpertMorningTime(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">午盘触发时间 (HH:MM)</label>
              <Input type="text" placeholder="13:15" className="h-8 text-xs mt-1"
                value={expertAfternoonTime}
                onChange={e => setExpertAfternoonTime(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">confidence 阈值 (0.5-1.0, 越高越严)</label>
              <Input type="number" step={0.05} min={0.5} max={1.0} className="h-8 text-xs mt-1"
                value={expertConfMin}
                onChange={e => setExpertConfMin(Number(e.target.value) || 0.75)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">熔断 PnL 阈值 (负数, 30min 内跌破自动 revert)</label>
              <Input type="number" step={50} max={0} className="h-8 text-xs mt-1"
                value={expertRevertPnl}
                onChange={e => setExpertRevertPnl(Number(e.target.value) || -200)} />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" className="text-xs h-8"
              onClick={() => saveExpertCfg?.mutate?.({
                enabled: expertEnabled, morningTime: expertMorningTime, afternoonTime: expertAfternoonTime,
                confidenceMin: expertConfMin, revertPnlThreshold: expertRevertPnl,
              })}>
              保存 LLM 自动应用配置
            </Button>
            <span className="text-[10px] text-muted-foreground">改配置立即生效 (cron 每分钟读最新)</span>
          </div>
        </CardContent>
      </Card>

      {/* ===== 交易模式控制（最醒目位置） ===== */}
      <Card className="bg-card border-2 border-orange-500/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-orange-500" />
            <span className="text-base font-bold text-orange-500">交易模式控制</span>
            <Badge
              className={
                tradeMode === 'simulation'
                  ? 'bg-gray-500 text-white ml-2'
                  : 'bg-red-600 text-white ml-2 animate-pulse'
              }
            >
              {tradeMode === 'simulation' ? '模拟' : '实盘交易'}
            </Badge>
          </CardTitle>
          <CardDescription className="text-xs text-orange-400/80">
            ⚠️ 仅在有底仓时才会执行实盘T+0，无底仓股票强制模拟。切换模式后须重启引擎生效。
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* [2026-06-02 删除 observe 模式] 两模式: simulation (含日切锚定真实持仓) / smtq (实盘) */}
          <div className="grid grid-cols-2 gap-3">
            {/* 模拟 (含日切锚定真实持仓) */}
            <button
              type="button"
              onClick={() => handleTradeModeChange('simulation')}
              className={[
                'rounded-lg border-2 p-4 text-left transition-all',
                tradeMode === 'simulation'
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-border/40 hover:border-blue-500/60',
              ].join(' ')}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🔘</span>
                <span className="font-bold text-sm text-blue-400">模拟</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                每日开盘锚定真实持仓为模拟起点，盘中虚拟买卖驱动 sim PnL，<strong className="text-blue-400">绝不发出真实委托</strong>。用真实 L2 行情。
              </p>
            </button>

            {/* 纯实盘 */}
            <button
              type="button"
              onClick={() => handleTradeModeChange('smtq')}
              className={[
                'rounded-lg border-2 p-4 text-left transition-all',
                tradeMode === 'smtq'
                  ? 'border-red-500 bg-red-500/10'
                  : 'border-border/40 hover:border-red-500/60',
              ].join(' ')}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">⚡</span>
                <span className="font-bold text-sm text-red-400">纯实盘</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                仅对<strong className="text-red-400">有底仓股票</strong>做T+0，买入量不超过底仓。无底仓股票强制模拟。
              </p>
            </button>
          </div>

          {tradeMode === 'smtq' && (
            <div className="mt-3 rounded-md bg-red-500/10 border border-red-500/30 px-4 py-2 text-xs text-red-400 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              实盘模式已选择——保存后须在来福机器手动创建授权文件，引擎重启后生效。
            </div>
          )}
        </CardContent>
      </Card>

      {/* 实盘二次确认弹窗 */}
      {showLiveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-card border-2 border-red-500 rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            {liveConfirmStep === 1 && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">⚠️</span>
                  <div>
                    <h2 className="text-lg font-bold text-red-400">切换实盘交易 - 第一步确认</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">请仔细阅读以下内容</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm mb-6">
                  <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 space-y-1.5">
                    <p className="font-medium text-red-400">实盘模式的限制：</p>
                    <p>• 仅对<strong>有真实底仓</strong>的股票执行T+0</p>
                    <p>• 买入量不得超过当前底仓数量</p>
                    <p>• 无底仓股票强制模拟，绝不新建仓位</p>
                    <p>• 引擎重启后授权自动失效，需重新确认</p>
                  </div>
                  <p className="text-muted-foreground text-xs">确认你了解以上规则并希望继续？</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowLiveConfirm(false); setLiveConfirmStep(0); setPendingTradeMode(null); }}
                    className="flex-1 rounded-lg border border-border/60 px-4 py-2 text-sm hover:bg-muted/40"
                  >取消</button>
                  <button
                    onClick={handleLiveAuthorizeConfirm}
                    className="flex-1 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 text-sm font-medium"
                  >我已了解，继续</button>
                </div>
              </>
            )}
            {liveConfirmStep === 2 && (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">🔴</span>
                  <div>
                    <h2 className="text-lg font-bold text-red-400">切换实盘交易 - 最终确认</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">此操作将立即生效</p>
                  </div>
                </div>
                <div className="rounded-md bg-red-500/15 border border-red-500/40 p-4 mb-6 text-sm text-red-300 space-y-1">
                  <p className="font-bold text-red-400">确认开启实盘交易？</p>
                  <p>引擎将立即切换为纯实盘模式，对有底仓的股票执行真实T+0委托。</p>
                  <p className="text-xs mt-2 text-muted-foreground">点击「确认开启实盘」后无法撤销，如需停止请切换回模拟模式。</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowLiveConfirm(false); setLiveConfirmStep(0); setPendingTradeMode(null); }}
                    className="flex-1 rounded-lg border border-border/60 px-4 py-2 text-sm hover:bg-muted/40"
                    disabled={authorizing}
                  >取消</button>
                  <button
                    onClick={handleLiveAuthorizeConfirm}
                    disabled={authorizing}
                    className="flex-1 rounded-lg bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-bold flex items-center justify-center gap-2"
                  >
                    {authorizing ? <><span className="animate-spin">⏳</span>授权中...</> : '确认开启实盘'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* 模块 A 参数 */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Search className="h-4 w-4 text-primary" />
            模块 A：T+0 选股参数
          </CardTitle>
          <CardDescription className="text-xs">筛选适合日内套利的高活跃度股票</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">日均成交额下限（亿）</Label>
              <Input
                type="number"
                value={form.minDailyAmount ?? 3}
                onChange={(e) => setField("minDailyAmount", +e.target.value)}
                className="h-9"
                step="0.5"
              />
              <p className="text-[10px] text-muted-foreground">过30天日均成交额</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">换手率下限（%）</Label>
              <Input
                type="number"
                value={form.minTurnoverRate ?? 3}
                onChange={(e) => setField("minTurnoverRate", +e.target.value)}
                className="h-9"
                step="0.5"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">换手率上限（%）</Label>
              <Input
                type="number"
                value={form.maxTurnoverRate ?? 15}
                onChange={(e) => setField("maxTurnoverRate", +e.target.value)}
                className="h-9"
                step="0.5"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">近5日平均振幅下限（%）</Label>
              <Input
                type="number"
                value={form.minAvgAmplitude ?? 3}
                onChange={(e) => setField("minAvgAmplitude", +e.target.value)}
                className="h-9"
                step="0.5"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">股价下限（元）</Label>
              <Input
                type="number"
                value={form.minPrice ?? 5}
                onChange={(e) => setField("minPrice", +e.target.value)}
                className="h-9"
                step="1"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">股价上限（元）</Label>
              <Input
                type="number"
                value={form.maxPrice ?? 50}
                onChange={(e) => setField("maxPrice", +e.target.value)}
                className="h-9"
                step="1"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">流通市值下限（亿）</Label>
              <Input
                type="number"
                value={form.minFloatMarketCap ?? 50}
                onChange={(e) => setField("minFloatMarketCap", +e.target.value)}
                className="h-9"
                step="10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">流通市值上限（亿）</Label>
              <Input
                type="number"
                value={form.maxFloatMarketCap ?? 500}
                onChange={(e) => setField("maxFloatMarketCap", +e.target.value)}
                className="h-9"
                step="50"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">涨跌幅下限（%）</Label>
              <Input
                type="number"
                value={form.minPctChange ?? -5}
                onChange={(e) => setField("minPctChange", +e.target.value)}
                className="h-9"
                step="0.5"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">涨跌幅上限（%）</Label>
              <Input
                type="number"
                value={form.maxPctChange ?? 5}
                onChange={(e) => setField("maxPctChange", +e.target.value)}
                className="h-9"
                step="0.5"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">量比下限</Label>
              <Input
                type="number"
                value={form.minVolumeRatio ?? 1}
                onChange={(e) => setField("minVolumeRatio", +e.target.value)}
                className="h-9"
                step="0.1"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">PE 上限</Label>
              <Input
                type="number"
                value={form.maxPeRatio ?? 50}
                onChange={(e) => setField("maxPeRatio", +e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">市值下限（亿元）</Label>
              <Input
                type="number"
                value={form.minMarketCap ?? 30}
                onChange={(e) => setField("minMarketCap", +e.target.value)}
                className="h-9"
              />
              <p className="text-[10px] text-muted-foreground">总市值预筛用</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">营收增长下限（%）</Label>
              <Input
                type="number"
                value={form.minRevenueGrowth ?? 10}
                onChange={(e) => setField("minRevenueGrowth", +e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">30日均成交额预筛（亿）</Label>
              <Input
                type="number"
                value={form.minAvgTurnover ?? 0.5}
                onChange={(e) => setField("minAvgTurnover", +e.target.value)}
                className="h-9"
                step="0.1"
              />
              <p className="text-[10px] text-muted-foreground">阶段一预筛用，可低于日均成交额下限</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">回溯天数</Label>
              <Input
                type="number"
                value={form.lookbackDays ?? 30}
                onChange={(e) => setField("lookbackDays", +e.target.value)}
                className="h-9"
              />
            </div>
          </div>
          <Separator className="bg-border/30" />
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch
                checked={(form.excludeST ?? 1) === 1}
                onCheckedChange={(v) => setField("excludeST", v ? 1 : 0)}
              />
              <Label className="text-xs">排除 ST/退市股</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={(form.aboveMa20 ?? 1) === 1}
                onCheckedChange={(v) => setField("aboveMa20", v ? 1 : 0)}
              />
              <Label className="text-xs">股价在 20 日均线上方</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.excludeLimitUpDown === 1}
                onCheckedChange={(v) => setField("excludeLimitUpDown", v ? 1 : 0)}
              />
              <Label className="text-xs">排除连续涨跌停</Label>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">连续涨跌停天数阈值</Label>
              <Input
                type="number"
                value={form.limitConsecutiveDays ?? 2}
                onChange={(e) => setField("limitConsecutiveDays", +e.target.value)}
                className="h-9 w-20"
              />
            </div>
          </div>
          <Separator className="bg-border/30" />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Activity className="h-3.5 w-3.5 text-orange-400" />
              <span className="text-xs font-medium">日内波段筛选条件</span>
            </div>
            <p className="text-[10px] text-muted-foreground">筛选过去N个交易日内，每日振幅超过M%的日内波段不少于P个的股票，确保选出的股票有足够的日内波动机会</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">观察天数 (N)</Label>
                <Input
                  type="number"
                  value={form.intradaySwingDays ?? 10}
                  onChange={(e) => setField("intradaySwingDays", +e.target.value)}
                  className="h-9"
                  min={3}
                  max={60}
                />
                <p className="text-[10px] text-muted-foreground">过去N个交易日</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">波段最小振幅 (M%)</Label>
                <Input
                  type="number"
                  value={form.intradaySwingMinAmplitude ?? 1.0}
                  onChange={(e) => setField("intradaySwingMinAmplitude", +e.target.value)}
                  className="h-9"
                  step="0.1"
                  min={0.3}
                  max={5}
                />
                <p className="text-[10px] text-muted-foreground">每个波段最小振幅%</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">每日最少波段数 (P)</Label>
                <Input
                  type="number"
                  value={form.intradaySwingMinCount ?? 3}
                  onChange={(e) => setField("intradaySwingMinCount", +e.target.value)}
                  className="h-9"
                  min={1}
                  max={20}
                />
                <p className="text-[10px] text-muted-foreground">每日至少P个合格波段</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 模块 B 参数 */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Newspaper className="h-4 w-4 text-primary" />
            模块 B：事件驱动参数
          </CardTitle>
          <CardDescription className="text-xs">AI 新闻分析与防追高过滤器</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">近3日涨幅上限（%）</Label>
              <Input
                type="number"
                value={form.max3dayGain ?? 15}
                onChange={(e) => setField("max3dayGain", +e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">MA5 乖离率上限（%）</Label>
              <Input
                type="number"
                value={form.maxMa5Deviation ?? 10}
                onChange={(e) => setField("maxMa5Deviation", +e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">每板块推荐龙头数</Label>
              <Input
                type="number"
                value={form.topStocksPerSector ?? 3}
                onChange={(e) => setField("topStocksPerSector", +e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">LLM 温度</Label>
              <Input
                type="number"
                value={form.llmTemperature ?? 0.3}
                onChange={(e) => setField("llmTemperature", +e.target.value)}
                className="h-9"
                step="0.1"
                min="0"
                max="2"
              />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch
                checked={form.yinnYangEnabled === 1}
                onCheckedChange={(v) => setField("yinnYangEnabled", v ? 1 : 0)}
              />
              <Label className="text-xs">启用 YINN/YANG 外盘共振</Label>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">共振权重</Label>
              <Input
                type="number"
                value={form.confidenceWeight ?? 0.2}
                onChange={(e) => setField("confidenceWeight", +e.target.value)}
                className="h-9 w-20"
                step="0.05"
                min="0"
                max="1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 模块 C: T+0 交易参数模板预览器 (Phase A2 - 2026-04-27) */}
      <ModuleCTemplatePreview />

      {/* 定时任务时间配置 */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            定时任务时间配置
          </CardTitle>
          <CardDescription className="text-xs">设置各模块自动执行的时间（北京时间，24小时制），设为 -1 表示禁用该时段</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* T+0 选股 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="h-3.5 w-3.5 text-blue-400" />
              <span className="text-xs font-medium">T+0 选股</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">第一次执行时间</Label>
                <Input
                  type="number"
                  value={form.scheduleScreeningTime1 ?? 16}
                  onChange={(e) => setField("scheduleScreeningTime1", +e.target.value)}
                  className="h-9"
                  min="-1"
                  max="23"
                />
                <p className="text-[10px] text-muted-foreground">默认 16:00（收盘后）</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">第二次执行时间</Label>
                <Input
                  type="number"
                  value={form.scheduleScreeningTime2 ?? -1}
                  onChange={(e) => setField("scheduleScreeningTime2", +e.target.value)}
                  className="h-9"
                  min="-1"
                  max="23"
                />
                <p className="text-[10px] text-muted-foreground">默认禁用（-1）</p>
              </div>
            </div>
          </div>

          <Separator className="bg-border/30" />

          {/* 短期掘金 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="h-3.5 w-3.5 text-amber-400" />
              <span className="text-xs font-medium">短期掘金</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">第一次执行时间</Label>
                <Input
                  type="number"
                  value={form.scheduleOpportunityTime1 ?? 8}
                  onChange={(e) => setField("scheduleOpportunityTime1", +e.target.value)}
                  className="h-9"
                  min="-1"
                  max="23"
                />
                <p className="text-[10px] text-muted-foreground">默认 8:00（开盘前）</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">第二次执行时间</Label>
                <Input
                  type="number"
                  value={form.scheduleOpportunityTime2 ?? 16}
                  onChange={(e) => setField("scheduleOpportunityTime2", +e.target.value)}
                  className="h-9"
                  min="-1"
                  max="23"
                />
                <p className="text-[10px] text-muted-foreground">默认 16:00（收盘后）</p>
              </div>
            </div>
          </div>

          <Separator className="bg-border/30" />

          {/* 长期机会 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
              <span className="text-xs font-medium">长期机会</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">第一次执行时间</Label>
                <Input
                  type="number"
                  value={form.scheduleLongTermTime1 ?? 8}
                  onChange={(e) => setField("scheduleLongTermTime1", +e.target.value)}
                  className="h-9"
                  min="-1"
                  max="23"
                />
                <p className="text-[10px] text-muted-foreground">默认 8:00（开盘前）</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">第二次执行时间</Label>
                <Input
                  type="number"
                  value={form.scheduleLongTermTime2 ?? 16}
                  onChange={(e) => setField("scheduleLongTermTime2", +e.target.value)}
                  className="h-9"
                  min="-1"
                  max="23"
                />
                <p className="text-[10px] text-muted-foreground">默认 16:00（收盘后）</p>
              </div>
            </div>
          </div>

          <Separator className="bg-border/30" />

          {/* 新闻定时获取 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Rss className="h-3.5 w-3.5 text-orange-400" />
              <span className="text-xs font-medium">新闻定时获取（东方财富 + 同花顺）</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">第一次</Label>
                <Input type="number" value={form.scheduleNewsTime1 ?? 7} onChange={(e) => setField("scheduleNewsTime1", +e.target.value)} className="h-9" min="-1" max="23" />
                <p className="text-[10px] text-muted-foreground">默认 7:30（开盘前）</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">第二次</Label>
                <Input type="number" value={form.scheduleNewsTime2 ?? 11} onChange={(e) => setField("scheduleNewsTime2", +e.target.value)} className="h-9" min="-1" max="23" />
                <p className="text-[10px] text-muted-foreground">默认 11:30（午休前）</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">第三次</Label>
                <Input type="number" value={form.scheduleNewsTime3 ?? 14} onChange={(e) => setField("scheduleNewsTime3", +e.target.value)} className="h-9" min="-1" max="23" />
                <p className="text-[10px] text-muted-foreground">默认 14:30（下午盘）</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">第四次</Label>
                <Input type="number" value={form.scheduleNewsTime4 ?? 20} onChange={(e) => setField("scheduleNewsTime4", +e.target.value)} className="h-9" min="-1" max="23" />
                <p className="text-[10px] text-muted-foreground">默认 20:30（晚间）</p>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">实际执行时间为设定小时的 30 分，如设 7 则在 7:30 执行。每次获取后自动去重。</p>
          </div>

          <Separator className="bg-border/30" />

          {/* 收盘前未做平提醒 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
              <span className="text-xs font-medium">收盘前未做平持仓提醒</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">提醒小时</Label>
                <Input
                  type="number"
                  value={form.scheduleUnfilledAlertHour ?? 14}
                  onChange={(e) => setField("scheduleUnfilledAlertHour", +e.target.value)}
                  className="h-9"
                  min="-1"
                  max="23"
                />
                <p className="text-[10px] text-muted-foreground">默认 14（即 14:30），-1 禁用</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">提醒分钟</Label>
                <Input
                  type="number"
                  value={form.scheduleUnfilledAlertMinute ?? 30}
                  onChange={(e) => setField("scheduleUnfilledAlertMinute", +e.target.value)}
                  className="h-9"
                  min="0"
                  max="59"
                />
                <p className="text-[10px] text-muted-foreground">默认 30（即 14:30）</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">行情延迟告警阈值（秒）</Label>
                <Input
                  type="number"
                  value={form.quoteStaleAlertSec ?? 60}
                  onChange={(e) => setField("quoteStaleAlertSec", +e.target.value)}
                  className="h-9"
                  min="0"
                  max="600"
                />
                <p className="text-[10px] text-muted-foreground">连续N秒价格不变则告警，0 禁用</p>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">每个交易日在设定时间自动检查运行中引擎是否有未做平持仓，如有则推送通知告警。</p>
          </div>

          <Separator className="bg-border/30" />

          {/* 盘中参数优化分析 */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Brain className="h-3.5 w-3.5 text-purple-400" />
              <span className="text-xs font-medium">盘中参数优化分析</span>
            </div>
            <p className="text-[10px] text-muted-foreground mb-3">
              T+0 引擎开盘后，三模型（Qwen+GPT+Sonnet）根据实时行情分析并给出参数微调建议（s1/s2/止损等12个参数）。
              建议在开盘 15 分钟后执行，此时有真实振幅/量比数据，调参效果最佳。
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">上午场 — 小时</Label>
                <Input
                  type="number"
                  value={form.scheduleIntradayHour1 ?? 9}
                  onChange={(e) => setField("scheduleIntradayHour1", +e.target.value)}
                  className="h-9"
                  min="9"
                  max="11"
                />
                <p className="text-[10px] text-muted-foreground">默认 9（时）</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">上午场 — 分钟</Label>
                <Input
                  type="number"
                  value={form.scheduleIntradayMinute1 ?? 45}
                  onChange={(e) => setField("scheduleIntradayMinute1", +e.target.value)}
                  className="h-9"
                  min="0"
                  max="59"
                />
                <p className="text-[10px] text-muted-foreground">默认 45（即 9:45）</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">下午场 — 小时</Label>
                <Input
                  type="number"
                  value={form.scheduleIntradayHour2 ?? 13}
                  onChange={(e) => setField("scheduleIntradayHour2", +e.target.value)}
                  className="h-9"
                  min="13"
                  max="14"
                />
                <p className="text-[10px] text-muted-foreground">默认 13（时）</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">下午场 — 分钟</Label>
                <Input
                  type="number"
                  value={form.scheduleIntradayMinute2 ?? 15}
                  onChange={(e) => setField("scheduleIntradayMinute2", +e.target.value)}
                  className="h-9"
                  min="0"
                  max="59"
                />
                <p className="text-[10px] text-muted-foreground">默认 15（即 13:15）</p>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              当前配置：上午 {String(form.scheduleIntradayHour1 ?? 9).padStart(2,'0')}:{String(form.scheduleIntradayMinute1 ?? 45).padStart(2,'0')} ·
              下午 {String(form.scheduleIntradayHour2 ?? 13).padStart(2,'0')}:{String(form.scheduleIntradayMinute2 ?? 15).padStart(2,'0')}
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <span>提示：时间为北京时间 24 小时制（0-23），设为 -1 可禁用该时段。修改后点击保存即时生效。</span>
          </div>
        </CardContent>
      </Card>

      {/* Agent 自动优化策略/参数 [Claude 2026-05-07] */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-400" />
            Agent 自动优化策略 / 参数
            <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-400 ml-auto">
              {(form.autoOptimizeEnabled ?? 0) === 1 ? "已启用" : "已关闭"}
            </Badge>
          </CardTitle>
          <CardDescription className="text-xs">
            盘中每 5 分钟基于实时T0打分自动调整 monitor 模板（保守/稳健/激进）和参数。仅 SIM/OBSERVE 模式生效，LIVE 模式禁用。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-xs">启用 Agent 自动优化</Label>
              <p className="text-[10px] text-muted-foreground">
                关闭：所有 monitor 保持用户设置的模板和参数<br />
                开启：每 5 分钟扫描运行中的 monitor，根据实时打分自动切换模板/参数/暂停
              </p>
            </div>
            <Switch
              checked={(form.autoOptimizeEnabled ?? 0) === 1}
              onCheckedChange={(v) => setField("autoOptimizeEnabled", v ? 1 : 0)}
            />
          </div>

          <div className="rounded-md bg-muted/30 border border-border/40 p-3 space-y-2">
            <div className="text-[11px] font-medium text-foreground">自动优化决策规则</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[10px] text-muted-foreground">
              <div>• 实时打分 ≥ 80 + 表现良好 → 升级 <span className="text-orange-400">aggressive</span> 抢机会</div>
              <div>• 实时打分 50-80 + 表现差 → 降级 <span className="text-green-400">balanced</span> 收紧</div>
              <div>• 实时打分 &lt; 50 → 强制 <span className="text-blue-400">conservative</span> + 提高止盈门槛</div>
              <div>• 实时打分 &lt; 30 + 无持仓 → <span className="text-red-400">暂停 monitor</span></div>
              <div>• 连续止损 ≥ 3 次 → <span className="text-red-400">暂停 monitor</span> 保护资金</div>
              <div>• 当日已盈利 ≥ 5 轮 → 收紧后续轮次防过度交易</div>
            </div>
            <div className="text-[10px] text-muted-foreground pt-1 border-t border-border/30">
              <Info className="h-3 w-3 inline mr-1" />
              同一 monitor 调整后冷却 1 小时；所有调整记录在 /agent 页面"自动优化日志"里可查
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LLM 调用限制与费用预警 */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Brain className="h-4 w-4 text-primary" />
            LLM 调用限制与费用预警
          </CardTitle>
          <CardDescription className="text-xs">控制 AI 分析的每日调用次数上限，以及月度费用预警阈值</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-xs">启用每日调用限制</Label>
              <p className="text-[10px] text-muted-foreground">限制每天 LLM 调用总次数，超出后将跳过 AI 分析</p>
            </div>
            <Switch
              checked={(form.llmDailyLimitEnabled ?? 1) === 1}
              onCheckedChange={(v) => setField("llmDailyLimitEnabled", v ? 1 : 0)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">每日调用上限</Label>
              <Input
                type="number"
                value={form.llmDailyLimit ?? 100}
                onChange={(e) => setField("llmDailyLimit", +e.target.value)}
                className="h-9"
                min="1"
                max="10000"
                disabled={(form.llmDailyLimitEnabled ?? 1) !== 1}
              />
              <p className="text-[10px] text-muted-foreground">默认 100 次/天</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 text-amber-400" />
                月度费用预警阈值（元）
              </Label>
              <Input
                type="number"
                value={form.costAlertThreshold ?? 50}
                onChange={(e) => setField("costAlertThreshold", +e.target.value)}
                className="h-9"
                min="1"
                step="10"
              />
              <p className="text-[10px] text-muted-foreground">当月 API 费用超过此值时发送预警通知</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 决策驾驶舱定时生成 */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-primary" />
            决策驾驶舱定时生成
          </CardTitle>
          <CardDescription className="text-xs">系统每天定时生成驾驶舱数据（全球指数、成交额、资金流向、板块热度、AI决策等），打开页面时直接读取缓存</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">第一次生成时间</Label>
              <Select value={String(form.scheduleDashboardTime1 ?? 8)} onValueChange={(v) => setField('scheduleDashboardTime1', Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">建议设置在开盘前，如 8:00</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">第二次生成时间</Label>
              <Select value={String(form.scheduleDashboardTime2 ?? 17)} onValueChange={(v) => setField('scheduleDashboardTime2', Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">建议设置在收盘后，如 17:00</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Timer className="h-3.5 w-3.5" />
            <span>提示：系统每天在设定时间自动生成驾驶舱数据，也可在概览页点击“手动刷新”即时生成</span>
          </div>
        </CardContent>
      </Card>

      {/* 底部保存按钮 */}
      <div className="flex justify-end pb-8">
        <Button onClick={handleSave} disabled={updateConfig.isPending} size="lg">
          {updateConfig.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          保存所有配置
        </Button>
      </div>
    </div>
  );
}
